/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import { StaggeredEntrance } from '@atlaskit/motion';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import type { ActiveContent } from '../../../types/activeContent';
import { MinifiedAudioPlayer } from '../audio-player/MinifiedAudioPlayer';

import { PlaylistEmptyView } from './PlaylistEmptyView';
import { PlaylistItem, type PlaylistItemData } from './PlaylistItem';
import { PlaylistUpNextEmptyView } from './PlaylistUpNextEmptyView';

const messages = defineMessages({
	nowPlaying: {
		id: 'ai-audio.playlist.now-playing',
		defaultMessage: 'Now playing',
		description: 'Header for the currently playing item section in the playlist',
	},
	upNext: {
		id: 'ai-audio.playlist.up-next',
		defaultMessage: 'Up next',
		description: 'Header for the upcoming items section in the playlist',
	},
} as const);

const styles = cssMap({
	playlist: {
		flex: 1,
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	sectionHeader: {
		textAlign: 'left',
		paddingLeft: token('space.100'),
		paddingTop: token('space.050'),
		paddingBottom: token('space.100'),
		paddingRight: token('space.050'),
		color: token('color.text.subtle'),
	},
	sectionList: {
		listStyleType: 'none',
		margin: '0',
		padding: '0',
	},
	upNextSection: {
		paddingTop: token('space.150'),
	},
	player: {
		marginLeft: token('space.050'),
		marginRight: token('space.050'),
	},
	emptySection: {
		paddingTop: token('space.200'),
	},
});

interface PlaylistViewProps {
	currentItem: ActiveContent | null;
	currentItemTrackType: TrackType | null;
	items: PlaylistItemData[];
	hasUnavailableItems?: boolean;
	onItemClick: (item: PlaylistItemData) => void;
}

export const PlaylistView = ({
	items,
	hasUnavailableItems = false,
	currentItem,
	currentItemTrackType,
	onItemClick,
}: PlaylistViewProps) => {
	const { formatMessage } = useIntl();
	const { sendTrackEvent } = usePlatformInstrumentation();

	useEffect(() => {
		sendTrackEvent({
			eventType: 'track',
			action: 'viewed',
			actionSubject: 'playlist',
			actionSubjectId: 'playlist',
			source: 'playlist',
			attributes: {
				playlistSize: items.length,
				hasUnavailableItems,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Separate currently playing item from the rest (only if it's in the playlist)
	const remainingItems =
		currentItem && currentItemTrackType
			? items.filter(
					(item) =>
						item.contentId !== currentItem.contentId || item.trackType !== currentItemTrackType,
				)
			: items;

	const renderPlaylistItem = useCallback(
		(item: PlaylistItemData, index: number, totalCount: number) => (
			<li key={item.id}>
				<PlaylistItem
					data={item}
					onItemClick={onItemClick}
					isFirstItem={currentItem ? index === 1 : index === 0}
					isLastItem={index === totalCount - 1}
					currentIndex={index}
					allItems={items}
				/>
			</li>
		),
		[onItemClick, currentItem, items],
	);

	return (
		<StaggeredEntrance columns={1}>
			<Flex xcss={styles.playlist} direction="column">
				{currentItem ? (
					<>
						{/* Now Playing Section */}
						<Box xcss={styles.sectionHeader}>
							<Heading as="h4" size="xxsmall">
								{formatMessage(messages.nowPlaying)}
							</Heading>
						</Box>
						<Stack as="ul" space="space.100" xcss={styles.sectionList}>
							<li key={currentItem.contentId} css={styles.player}>
								<MinifiedAudioPlayer title={currentItem.title || ''} byline="" elevated />
							</li>
						</Stack>

						{/* Up Next Section */}
						<Box xcss={styles.upNextSection}>
							<Box xcss={styles.sectionHeader}>
								<Heading as="h4" size="xxsmall">
									{formatMessage(messages.upNext)}
								</Heading>
							</Box>
							{remainingItems.length > 0 ? (
								<Stack as="ul" space="space.100" xcss={styles.sectionList}>
									{remainingItems.map((item, index) => {
										return renderPlaylistItem(item, index + 1, items.length);
									})}
								</Stack>
							) : (
								<Flex xcss={styles.emptySection}>
									{FeatureGates.getExperimentValue(
										'confluence_audio_updated_playlist_empty_state',
										'isEnabled',
										false,
									) ? (
										<PlaylistUpNextEmptyView />
									) : (
										<PlaylistEmptyView />
									)}
								</Flex>
							)}
						</Box>
					</>
				) : (
					/* Nothing is playing, show all items */
					<Stack as="ul" space="space.100" xcss={styles.sectionList}>
						{items.map((item, index) => renderPlaylistItem(item, index, items.length))}
					</Stack>
				)}
			</Flex>
		</StaggeredEntrance>
	);
};
